import { useEffect, useRef, useCallback } from 'react'
import { notify, NotificationLevel } from '@retailer-platform/shared-components'
import { useTheme } from '@retailer-platform/shared-components/src/tds'
import { NotificationIcon } from '@instacart/ids-core'
import { useDomainMessages } from '../utils/domain/intl'
import { useNotificationsQuery } from './useNotificationsQuery'

interface UseUnreadNotificationsWithToastOptions {
  pollingInterval?: number
}

export const useUnreadNotificationsWithToast = ({
  pollingInterval,
}: UseUnreadNotificationsWithToastOptions) => {
  const theme = useTheme()
  const previousUnreadCount = useRef<number | null>(null)
  const hasInitialized = useRef(false)

  // Use the existing unread count query
  const { data: unreadData, refetch } = useNotificationsQuery({
    pageSize: 1,
    isRead: false,
    pollingInterval,
  })

  const totalUnreadCount = unreadData?.notifications?.meta?.totalCount ?? 0
  const hasData = unreadData?.notifications?.meta !== undefined

  // Get i18n messages at hook level
  const i18n = useDomainMessages({
    newNotification: 'notificationsDomain.toast.newNotification',
    newNotifications: 'notificationsDomain.toast.newNotifications',
  })

  // Toast logic - show toasts when polling is enabled
  useEffect(() => {
    if (!pollingInterval || !hasData) {
      return
    }

    // On first load, just store the count without showing toast
    if (!hasInitialized.current) {
      previousUnreadCount.current = totalUnreadCount
      hasInitialized.current = true
      return
    }

    // Show toast if unread count increased
    if (previousUnreadCount.current !== null && totalUnreadCount > previousUnreadCount.current) {
      const newNotificationCount = totalUnreadCount - previousUnreadCount.current
      const hasMultipleNotifications = newNotificationCount > 1

      let toastContent: string
      if (hasMultipleNotifications) {
        // For multiple notifications, replace the placeholder with actual count
        toastContent = i18n.newNotifications.replace('{count}', newNotificationCount.toString())
      } else {
        toastContent = i18n.newNotification
      }

      notify({
        contents: toastContent,
        level: NotificationLevel.Info,
        icon: (
          <NotificationIcon
            css={{
              fill: theme.colors.systemInfoRegular,
            }}
            size={28}
          />
        ),
        timeout: 5000,
      })
    }

    // Update the previous count for next comparison
    previousUnreadCount.current = totalUnreadCount
  }, [totalUnreadCount, pollingInterval, hasData, i18n, theme])

  // Reset when polling is disabled
  useEffect(() => {
    if (!pollingInterval) {
      hasInitialized.current = false
      previousUnreadCount.current = null
    }
  }, [pollingInterval])

  return {
    totalUnreadCount,
    refetch,
    onDrawerOpen: refetch,
  }
}
